import React, { useCallback, useMemo } from "react";

import { FilterExpression, FilterType, OperatorType } from "../../../../Helpers/FilterExpressions/FilterExpression";
import { FilterSourceInfoType, getFilterBySourceId, MultiLineFilter } from "../../MultiLine/MultiLineFilter";
import { AbsoluteModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { PathResolver, withResolvePaths } from "../../../withResolvePaths";
import { ICandyApiDraftClient } from "../../../../../../Common/CandyApiClient/ICandyApiClient";
import { withCandyApiDraftClient } from "../../../withCandyApiDraftClient";
import { useTypedDispatch } from "../../../../../../Common/UseTypedSelector";
import { useMultilineClient } from "../../../../Engine/Core/Filtering/UseMultilineClient";
import { createHocDisplayName } from "../../../TypingUtils";

import { FilterInput, FilterInputProps } from "./FilterInput";
import { defaultValueForServerSearchPreparer } from "./WellKnownPatternsPrepareHelper";

const maxInstanceCountForServerMode = 1000;

interface FilterInputContainerProps extends FilterInputProps {
    multilinePath: AbsoluteModelPath;
    dataPaths: AbsoluteModelPath[];
    type: FilterType;
    candyApiDraftClient: ICandyApiDraftClient;
    serverMode?: boolean;
    resolvePath: PathResolver;
    filteredUniqKey?: string;
}

const buildInstanceFilterExpression = async (
    candyApiDraftClient: ICandyApiDraftClient,
    value: string,
    multilinePath: AbsoluteModelPath,
    dataPaths: AbsoluteModelPath[]
): Promise<FilterExpression | undefined> => {
    const getFilter = async (): Promise<FilterExpression> => {
        const legacyMultilinePath = multilinePath.toLegacyPath();
        const instances = await candyApiDraftClient.searchInstancesByText(
            defaultValueForServerSearchPreparer(value),
            legacyMultilinePath,
            dataPaths.map(x => x.toLegacyPath().replace(`${legacyMultilinePath}/*/`, "")),
            maxInstanceCountForServerMode
        );
        return {
            operator: OperatorType.ByInstances,
            condition: {
                dataPath: legacyMultilinePath,
                instances: instances,
            },
        };
    };

    return value != undefined && value !== "" ? await getFilter() : undefined;
};

const FilterInputContainerWrapper: React.FC<FilterInputContainerProps> = ownProps => {
    const {
        multilinePath,
        dataPaths,
        type,
        candyApiDraftClient,
        serverMode,
        resolvePath,
        filteredUniqKey = "default",
    } = ownProps;
    const dispatch = useTypedDispatch();
    const basePath = resolvePath(multilinePath);
    const { client, filters } = useMultilineClient(basePath, filteredUniqKey, info => ({
        client: info.client,
        filters: info.multilineState.filters,
    }));

    const { value, resolvedMultilinePath, resolvedDataPaths } = useMemo(() => {
        const innerResolvedDataPaths = dataPaths.map(path => resolvePath(path));
        const currentFilter = getFilterBySourceId(filters, innerResolvedDataPaths.map(x => x.toLegacyPath()).join("_"));
        const currentFilterInfo =
            currentFilter?.filterSourceInfo?.type === FilterSourceInfoType.String
                ? currentFilter?.filterSourceInfo
                : undefined;

        return {
            value: currentFilterInfo?.value ?? "",
            resolvedMultilinePath: basePath,
            resolvedDataPaths: innerResolvedDataPaths,
        };
    }, [filters, dataPaths, serverMode]);

    const onBuildInstanceFilterExpression = useCallback(
        (currentValue: string) =>
            buildInstanceFilterExpression(candyApiDraftClient, currentValue, multilinePath, dataPaths),
        [candyApiDraftClient, multilinePath, dataPaths]
    );

    const onChangeFilter = useCallback(
        (_stickyTablePath: string, multilineFilter: MultiLineFilter) => client.setFilter(multilineFilter),
        [dispatch, resolvedMultilinePath, filteredUniqKey]
    );

    const onRemoveFilter = useCallback(
        (_stickyTablePath: string, filterSourceId: string) => client.removeFilter(filterSourceId),
        [dispatch, resolvedMultilinePath, filteredUniqKey]
    );

    const customFilterProps = serverMode ? { onBuildCustomFilterExpression: onBuildInstanceFilterExpression } : {};

    return (
        <FilterInput
            {...ownProps}
            value={value}
            runSearchOnlyOnEnter={serverMode}
            multilinePath={resolvedMultilinePath}
            dataPaths={resolvedDataPaths}
            onChangeFilter={onChangeFilter}
            onRemoveFilter={onRemoveFilter}
            type={type}
            {...customFilterProps}
        />
    );
};
FilterInputContainerWrapper.displayName = createHocDisplayName(FilterInput, "FilterInputContainerWrapper");

export const FilterInputContainer = React.memo(withResolvePaths(withCandyApiDraftClient(FilterInputContainerWrapper)));
